import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const CaloriesBurned = () => {
  // State variables for the calories burned calculation
  const [met, setMet] = useState("");
  const [weight, setWeight] = useState("");
  const [duration, setDuration] = useState("");
  const [caloriesBurned, setCaloriesBurned] = useState(0);

  // Event handlers to update state values
  const handleMetChange = (e) => setMet(parseFloat(e.target.value) || 0);
  const handleWeightChange = (e) => setWeight(parseFloat(e.target.value) || 0);
  const handleDurationChange = (e) =>
    setDuration(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate calories burned when any input changes
  useEffect(() => {
    if (met && weight && duration) {
      const calories = met * weight * duration;
      setCaloriesBurned(calories);
    } else {
      setCaloriesBurned(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [met, weight, duration]); // Dependency array: runs when any of the inputs change

  return (
    <Layout title="Calories Burned Calculator" path="calories-burned">
      <section className="section-padding" id="calories-burned-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Calories Burned Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Calories Burned Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* MET Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="met" className="form-label">
                        MET (Metabolic Equivalent)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>MET (Metabolic Equivalent):</strong> A measure of the energy cost of physical activity. For example, 1 MET represents the energy expended at rest, while higher values correspond to more intense activities."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="met"
                        placeholder="Enter MET value"
                        value={met}
                        onChange={handleMetChange}
                      />
                    </div>

                    {/* Weight Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="weight" className="form-label">
                        Weight (kg)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Weight (kg):</strong> Your body weight in kilograms. It influences the number of calories burned during an activity."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="weight"
                        placeholder="Enter your weight"
                        value={weight}
                        onChange={handleWeightChange}
                      />
                    </div>

                    {/* Duration Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="duration" className="form-label">
                        Duration (hours)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Duration (hours):</strong> The total time you spent performing the activity, measured in hours."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="duration"
                        placeholder="Enter duration of exercise"
                        value={duration}
                        onChange={handleDurationChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Calories Burned:{" "}
                        <span className="fw-bold ms-3">
                          {caloriesBurned.toFixed(2)} kcal
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Calories Burned Calculator
                </h3>

                {/* What is a Calories Burned Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Calories Burned Calculator?
                </h4>
                <p>
                  A **Calories Burned Calculator** helps individuals estimate
                  the number of calories burned during a workout or exercise
                  based on the MET (Metabolic Equivalent), body weight, and
                  exercise duration.
                </p>

                {/* Why Use a Calories Burned Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Calories Burned Calculator?
                </h4>
                <p>
                  This calculator helps individuals track their fitness
                  progress, set workout goals, and manage weight loss or weight
                  maintenance plans by estimating the calories burned during
                  exercises.
                </p>

                {/* Benefits of Using a Calories Burned Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Calories Burned Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Supports Fitness Tracking:</strong> Helps
                    individuals monitor their calorie expenditure during
                    workouts.
                  </li>
                  <li>
                    <strong>Optimizes Workout Routines:</strong> Assists in
                    designing personalized workout plans based on calorie burn
                    goals.
                  </li>
                  <li>
                    <strong>Helps with Weight Management:</strong> Useful for
                    individuals aiming for weight loss or weight gain.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Calories Burned Calculator?
                </h4>
                <p>The calories burned calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Fitness Enthusiasts:</strong> Track calories burned
                    during various workouts.
                  </li>
                  <li>
                    <strong>Gym Goers:</strong> Helps individuals optimize their
                    fitness routines.
                  </li>
                  <li>
                    <strong>Health Coaches and Trainers:</strong> Assist clients
                    in creating personalized fitness plans.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is MET?</h4>
                <p>
                  MET (Metabolic Equivalent of Task) is a unit used to estimate
                  the amount of energy expended during physical activity. A MET
                  value of 1 represents the energy expended while at rest, and
                  higher MET values correspond to higher intensity activities.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is it important to track calories burned?
                </h4>
                <p>
                  Tracking calories burned is essential for individuals aiming
                  to maintain, lose, or gain weight. It helps to ensure that
                  exercise routines are effectively contributing to achieving
                  fitness or weight goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CaloriesBurned;
